import { globalRef } from '@amazon/vinyl-util'
import { documentEvents } from '@/util/interaction'

type SinkElement = HTMLMediaElement & {
    readonly sinkId: string
    setSinkId(id: string): Promise<void>
}

const sink = globalRef(() => ({
    id: '',
    elements: new Set<SinkElement>(),
    listening: false,
}))

/**
 * Whether this browser plays a media element on a chosen output device and lists the devices:
 * `setSinkId` and `enumerateDevices`. Safari and the desktop app's WKWebView have no `setSinkId`.
 */
export function webAudioOutputSupported(): boolean {
    return (
        typeof HTMLMediaElement !== 'undefined' &&
        'setSinkId' in HTMLMediaElement.prototype &&
        'mediaDevices' in navigator &&
        typeof navigator.mediaDevices.enumerateDevices === 'function'
    )
}

function isSinkElement(target: EventTarget | null): target is SinkElement {
    return typeof (target as Partial<SinkElement> | null)?.setSinkId === 'function'
}

function apply(el: SinkElement): Promise<void> {
    return el.sinkId === sink.value.id ? Promise.resolve() : el.setSinkId(sink.value.id)
}

function applyLater(el: HTMLMediaElement): void {
    // A refused device is reported by setAudioOutput, which the picker's next choice corrects.
    apply(el as SinkElement).catch(() => {})
}

/** Plays `el`, a media element the web player owns, on the chosen output device from now on. */
export function followAudioOutput(el: HTMLMediaElement): void {
    if (!webAudioOutputSupported()) return
    sink.value.elements.add(el as SinkElement)
    applyLater(el)
}

/**
 * Plays every media element of the page on the output device `id` (`''`: the system default):
 * the followed ones, those in the document, and those that start playing later. Rejects when the
 * browser refuses the device (gone, or never exposed to this page).
 */
export async function setAudioOutput(id: string): Promise<void> {
    const state = sink.value
    state.id = id
    if (!state.listening) {
        state.listening = true
        // Capture: `play` does not bubble, and a library's own element (the Audible SDK's) is never followed.
        documentEvents.on(
            'play',
            (event) => {
                if (isSinkElement(event.target)) applyLater(event.target)
            },
            { capture: true }
        )
    }
    const inDocument = document.querySelectorAll<SinkElement>('audio, video')
    await Promise.all([...new Set([...state.elements, ...inDocument])].map(apply))
}
